/**
 * TasksConfig — manage admin tasks.
 * Each task carries its stage bracket (unblocks_at_stage → must_complete_by_stage),
 * automatability flags, and automation_enabled flag (system vs admin driven).
 */

import { useEffect, useMemo, useState } from 'react';
import { Edit3, Plus, RefreshCw, X } from 'lucide-react';
import { createTask, getStages, getTasks, updateTask } from '../api';
import type { Stage, Task } from '../types';

export default function TasksConfig() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [tRes, sRes] = await Promise.all([getTasks(includeArchived), getStages(false)]);
    if (!tRes.success || !tRes.data || !sRes.success || !sRes.data) {
      setError(tRes.error || sRes.error || 'Failed to load tasks.');
      setTasks([]);
      setStages([]);
    } else {
      setTasks(tRes.data);
      setStages(sRes.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [includeArchived]);

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(null), 3000);
  };

  const stageById = useMemo(() => {
    const m = new Map<string, Stage>();
    for (const s of stages) m.set(s.id, s);
    return m;
  }, [stages]);

  const handleArchiveToggle = async (task: Task) => {
    const r = await updateTask(task.id, { archived: !task.archived_at });
    if (!r.success) {
      showToast(r.error || 'Failed to update');
      return;
    }
    await load();
    showToast(task.archived_at ? 'Task unarchived.' : 'Task archived.');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Admin tasks</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Each task is bounded by a stage range and can be admin-driven or system-automated.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show archived
          </label>
          <button
            onClick={load}
            className="p-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add task
          </button>
        </div>
      </div>

      {toast && (
        <div className="mb-3 px-3 py-1.5 bg-green-50 border border-green-200 text-green-800 text-sm rounded">
          {toast}
        </div>
      )}

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
          {error}
        </div>
      ) : loading ? (
        <div className="text-center py-8 text-gray-500">
          <RefreshCw className="w-4 h-4 inline-block animate-spin mr-2" />
          Loading…
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Pos</th>
                <th className="px-3 py-2 text-left font-medium">Slug</th>
                <th className="px-3 py-2 text-left font-medium">Display name</th>
                <th className="px-3 py-2 text-left font-medium">Unblocks at</th>
                <th className="px-3 py-2 text-left font-medium">Must complete by</th>
                <th className="px-3 py-2 text-left font-medium">Driver</th>
                <th className="px-3 py-2 text-left font-medium">State</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-gray-500 italic">
                    No tasks yet.
                  </td>
                </tr>
              ) : (
                tasks.map((t) => (
                  <tr key={t.id} className={t.archived_at ? 'bg-gray-50' : ''}>
                    <td className="px-3 py-2 text-gray-700">{t.position}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{t.slug}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {t.display_name}
                      {t.description && (
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {t.description}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700 text-xs">
                      {stageById.get(t.unblocks_at_stage_id)?.display_name || (
                        <span className="text-red-600 italic">missing</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700 text-xs">
                      {stageById.get(t.must_complete_by_stage_id)?.display_name || (
                        <span className="text-red-600 italic">missing</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {t.automation_enabled ? (
                        <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded">
                          System
                        </span>
                      ) : (
                        <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                          Admin
                        </span>
                      )}
                      {t.automatable && !t.automation_enabled && (
                        <span
                          className="ml-1 text-xs text-gray-500 italic"
                          title="Automatable in a future phase"
                        >
                          (auto-ready)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {t.archived_at ? (
                        <span className="text-xs text-gray-500 italic">Archived</span>
                      ) : (
                        <span className="text-xs text-green-700">Active</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setEditing(t)}
                          className="p-1 text-gray-500 hover:text-gray-900"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleArchiveToggle(t)}
                          className="p-1 text-gray-500 hover:text-gray-900"
                          title={t.archived_at ? 'Unarchive' : 'Archive'}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <TaskEditor
          mode="create"
          stages={stages}
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
            showToast('Task created.');
          }}
        />
      )}

      {editing && (
        <TaskEditor
          mode="edit"
          task={editing}
          stages={stages}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
            showToast('Task updated.');
          }}
        />
      )}
    </div>
  );
}

function TaskEditor({
  mode,
  task,
  stages,
  onCancel,
  onSaved,
}: {
  mode: 'create' | 'edit';
  task?: Task;
  stages: Stage[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Partial<Task>>({
    slug: task?.slug || '',
    display_name: task?.display_name || '',
    description: task?.description || '',
    unblocks_at_stage_id: task?.unblocks_at_stage_id || stages[0]?.id,
    must_complete_by_stage_id: task?.must_complete_by_stage_id || stages[stages.length - 1]?.id,
    automatable: task?.automatable ?? false,
    automation_enabled: task?.automation_enabled ?? false,
    position: task?.position ?? 100,
    expected_duration_hours: task?.expected_duration_hours ?? null,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    const payload: any = { ...form };
    if (payload.description === '') payload.description = null;

    const r =
      mode === 'create' ? await createTask(payload) : await updateTask(task!.id, payload);
    setSaving(false);
    if (!r.success) {
      setErr(r.error || 'Save failed');
      return;
    }
    onSaved();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          {mode === 'create' ? 'New task' : `Edit ${task?.display_name}`}
        </h3>
        {err && (
          <div className="mb-3 px-3 py-1.5 bg-red-50 border border-red-200 text-red-800 text-sm rounded">
            {err}
          </div>
        )}
        <div className="space-y-3">
          <Field label="Slug">
            <input
              type="text"
              value={form.slug || ''}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              disabled={mode === 'edit'}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md font-mono disabled:bg-gray-50 disabled:text-gray-500"
            />
          </Field>
          <Field label="Display name">
            <input
              type="text"
              value={form.display_name || ''}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
          </Field>
          <Field label="Description (admin instructions)">
            <textarea
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Unblocks at stage">
              <select
                value={form.unblocks_at_stage_id || ''}
                onChange={(e) => setForm({ ...form, unblocks_at_stage_id: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              >
                {stages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.display_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Must complete by stage">
              <select
                value={form.must_complete_by_stage_id || ''}
                onChange={(e) => setForm({ ...form, must_complete_by_stage_id: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              >
                {stages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.display_name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Position">
              <input
                type="number"
                value={form.position ?? ''}
                onChange={(e) =>
                  setForm({ ...form, position: parseInt(e.target.value, 10) || 0 })
                }
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              />
            </Field>
            <Field label="Expected duration (hours)">
              <input
                type="number"
                value={form.expected_duration_hours ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    expected_duration_hours: e.target.value === '' ? null : parseInt(e.target.value, 10),
                  })
                }
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              />
            </Field>
          </div>
          <div className="space-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.automatable}
                onChange={(e) => setForm({ ...form, automatable: e.target.checked })}
              />
              <span>
                Automatable{' '}
                <span className="text-xs text-gray-500">
                  (informational — could be automated)
                </span>
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.automation_enabled}
                onChange={(e) => setForm({ ...form, automation_enabled: e.target.checked })}
              />
              <span>
                Automation enabled{' '}
                <span className="text-xs text-gray-500">
                  (system drives this — flips status without admin)
                </span>
              </span>
            </label>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !form.slug || !form.display_name}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
